import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Mail, Phone } from 'lucide-react';
import Logo from '../components/Logo';
import Footer from '../components/Footer';

/**
 * AboutPage — public, unauthenticated "About Us" page at /about.
 * Design: black-and-white minimalist; full-width dark hero and CTA section
 * bookending three light content sections. Max content width 760px to keep
 * the read line comfortable on large screens.
 *
 * Copy is verbatim from the spec — do not edit without product approval.
 */
export default function AboutPage() {
    const navigate = useNavigate();

    // Set the browser tab title; reset on unmount so other pages don't inherit.
    useEffect(() => {
        const prev = document.title;
        document.title = 'About Us — GulfBridge';
        return () => { document.title = prev; };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col" data-testid="about-page">
            {/* Top utility bar — keeps back navigation + brand consistent
                with the other public pages (FAQ, Contact). */}
            <header className="border-b border-zinc-200 bg-white sticky top-0 z-10">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 text-sm font-semibold"
                        data-testid="about-back-btn"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/')}
                        className="hover:opacity-70 transition-opacity"
                        data-testid="about-logo"
                        aria-label="GulfBridge home"
                    >
                        <Logo size="sm" />
                    </button>
                </div>
            </header>

            {/* SECTION 1 — HERO */}
            <section
                className="bg-[#111111] text-white px-5 sm:px-6 py-16 sm:py-24 text-center"
                data-testid="about-hero"
            >
                <div className="max-w-3xl mx-auto">
                    <h1 className="font-heading font-black tracking-tight text-[28px] sm:text-5xl leading-tight">
                        About GulfBridge
                    </h1>
                    <p className="mt-3 sm:mt-4 text-base sm:text-xl text-zinc-300 font-light">
                        Bridging opportunities across continents.
                    </p>
                </div>
            </section>

            {/* SECTION 2 — OUR MISSION */}
            <section className="bg-white px-5 sm:px-6 py-14 sm:py-20" data-testid="about-mission">
                <div className="max-w-[760px] mx-auto">
                    <p className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-3">
                        OUR MISSION
                    </p>
                    <h2 className="font-heading font-black text-2xl sm:text-3xl text-zinc-900 mb-4">
                        Connecting talent across borders
                    </h2>
                    <p className="text-[15px] sm:text-[17px] leading-relaxed text-zinc-700">
                        GulfBridge exists to connect hardworking people from around the world with meaningful
                        employment opportunities in the Gulf region. We believe talent has no borders — and that
                        the right tools can make cross-border hiring simple, safe, and dignified for everyone involved.
                    </p>
                </div>
            </section>

            <Divider />

            {/* SECTION 3 — WHAT WE DO */}
            <section className="bg-white px-5 sm:px-6 py-14 sm:py-20" data-testid="about-what-we-do">
                <div className="max-w-[760px] mx-auto">
                    <p className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-3">
                        WHAT WE DO
                    </p>
                    <h2 className="font-heading font-black text-2xl sm:text-3xl text-zinc-900 mb-4">
                        A platform built for the full hiring journey
                    </h2>
                    <p className="text-[15px] sm:text-[17px] leading-relaxed text-zinc-700">
                        GulfBridge is a technology platform that handles the full hiring journey — from worker
                        discovery and verification through to job offers, visa tracking, and travel documentation.
                        We built GulfBridge to bring structure, transparency, and trust to a process that has
                        historically been complicated and fragmented.
                    </p>
                </div>
            </section>

            {/* SECTION 4 — WHO WE SERVE (subtle grey background) */}
            <section className="bg-[#F9FAFB] px-5 sm:px-6 py-14 sm:py-20" data-testid="about-who-we-serve">
                <div className="max-w-[760px] mx-auto">
                    <p className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-3">
                        WHO WE SERVE
                    </p>
                    <h2 className="font-heading font-black text-2xl sm:text-3xl text-zinc-900 mb-4">
                        Workers worldwide. Employers in the Gulf.
                    </h2>
                    <p className="text-[15px] sm:text-[17px] leading-relaxed text-zinc-700 mb-8">
                        We serve blue collar workers from around the world who are ready to bring their skills to
                        the Gulf, and Gulf-based employers who need reliable, verified talent. Our platform connects
                        workers with employers across the UAE, Saudi Arabia, Qatar, Kuwait, Bahrain, and Oman.
                    </p>

                    {/* Two cards: stack on mobile, side-by-side from md+ */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                        <article
                            className="bg-white border border-[#E5E7EB] rounded-lg p-6"
                            data-testid="about-card-workers"
                        >
                            <h3 className="font-heading font-bold text-lg text-zinc-900 mb-2">Workers</h3>
                            <p className="text-[15px] sm:text-base leading-relaxed text-zinc-700">
                                We welcome blue collar workers from every country who are ready to bring their skills to the Gulf region.
                            </p>
                        </article>

                        <article
                            className="bg-white border border-[#E5E7EB] rounded-lg p-6"
                            data-testid="about-card-employers"
                        >
                            <h3 className="font-heading font-bold text-lg text-zinc-900 mb-2">Employers</h3>
                            <p className="text-[15px] sm:text-base leading-relaxed text-zinc-700">
                                We connect Gulf-based employers across the UAE, Saudi Arabia, Qatar, Kuwait, Bahrain,
                                and Oman with verified, work-ready talent.
                            </p>
                        </article>
                    </div>
                </div>
            </section>

            {/* SECTION 5 — BEHIND GULFBRIDGE */}
            <section className="bg-white px-5 sm:px-6 py-14 sm:py-20" data-testid="about-behind">
                <div className="max-w-[760px] mx-auto">
                    <p className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-3">
                        BEHIND GULFBRIDGE
                    </p>
                    <h2 className="font-heading font-black text-2xl sm:text-3xl text-zinc-900 mb-4">
                        Built by TARKiiZ Communications Inc.
                    </h2>
                    <p className="text-[15px] sm:text-[17px] leading-relaxed text-zinc-700 mb-6">
                        GulfBridge is a product of TARKiiZ Communications Inc., a company incorporated in the province
                        of Ontario, Canada.
                    </p>

                    {/* Registration info box — light grey background, subtle border */}
                    <dl
                        className="bg-[#F3F4F6] border border-[#E5E7EB] rounded-lg px-4 sm:px-5 py-4 sm:py-5 text-sm sm:text-[15px]"
                        data-testid="about-registration-info"
                    >
                        <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 py-1">
                            <dt className="font-medium text-zinc-900 sm:w-56 shrink-0">Company:</dt>
                            <dd className="text-zinc-700">TARKiiZ Communications Inc.</dd>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 py-1">
                            <dt className="font-medium text-zinc-900 sm:w-56 shrink-0">Ontario Corporation Number:</dt>
                            <dd className="text-zinc-700">1000158035</dd>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 py-1">
                            <dt className="font-medium text-zinc-900 sm:w-56 shrink-0">Province:</dt>
                            <dd className="text-zinc-700">Ontario, Canada</dd>
                        </div>
                    </dl>
                </div>
            </section>

            {/* SECTION 5.5 — JOIN CTA (light, sits between Behind GulfBridge and dark Get In Touch) */}
            <section
                className="bg-white px-5 sm:px-6 py-14 sm:py-20 border-t border-zinc-100 text-center"
                data-testid="about-cta"
            >
                <div className="max-w-[760px] mx-auto">
                    <h2 className="font-heading font-black text-2xl sm:text-3xl text-zinc-900 mb-3">
                        Ready to join GulfBridge?
                    </h2>
                    <p className="text-[15px] sm:text-base text-zinc-600 mb-7 max-w-md mx-auto">
                        Whether you're looking for work in the Gulf or looking to hire verified talent — get started in minutes.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <button
                            type="button"
                            onClick={() => navigate('/auth?tab=register&role=worker')}
                            className="inline-flex items-center justify-center gap-2 bg-zinc-900 text-white text-sm font-semibold px-6 h-12 hover:bg-zinc-800 transition-colors"
                            data-testid="about-cta-worker-btn"
                        >
                            Join GulfBridge
                            <ArrowRight className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate('/auth?tab=register&role=employer')}
                            className="inline-flex items-center justify-center gap-2 bg-white text-zinc-900 border border-zinc-900 text-sm font-semibold px-6 h-12 hover:bg-zinc-50 transition-colors"
                            data-testid="about-cta-employer-btn"
                        >
                            Hire on GulfBridge
                        </button>
                    </div>
                </div>
            </section>

            {/* SECTION 6 — GET IN TOUCH (mirrors hero) */}
            <section
                className="bg-[#111111] text-white px-5 sm:px-6 py-14 sm:py-20 text-center"
                data-testid="about-contact"
            >
                <div className="max-w-3xl mx-auto">
                    <p className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-zinc-400 font-light mb-3">
                        GET IN TOUCH
                    </p>
                    <h2 className="font-heading font-black text-2xl sm:text-4xl mb-3">
                        Have a question or want to learn more?
                    </h2>
                    <p className="text-[15px] sm:text-lg text-zinc-300 mb-8">
                        We would love to hear from you.
                    </p>

                    <div className="space-y-3 text-[15px] sm:text-base">
                        <p className="flex items-center justify-center gap-2">
                            <Mail className="w-4 h-4 text-zinc-400" aria-label="Email" />
                            <a
                                href="mailto:contact@gulfbridge.ca"
                                className="text-white hover:underline font-medium"
                                data-testid="about-email-link"
                            >
                                contact@gulfbridge.ca
                            </a>
                        </p>
                        <p className="flex items-center justify-center gap-2">
                            <Phone className="w-4 h-4 text-zinc-400" aria-label="Phone / WhatsApp" />
                            <a
                                href="tel:+15819072892"
                                className="text-white hover:underline font-medium"
                                data-testid="about-phone-link"
                            >
                                +1-581-907-2892
                            </a>
                        </p>
                    </div>

                    <p className="mt-10 text-xs sm:text-sm text-zinc-500" data-testid="about-footer-attribution">
                        GulfBridge is a product of TARKiiZ Communications Inc. — Ontario, Canada
                    </p>
                </div>
            </section>
            <Footer />
        </div>
    );
}

function Divider() {
    // Subtle full-width divider between content sections; aligned to content width.
    return (
        <div className="px-5 sm:px-6">
            <div className="max-w-[760px] mx-auto border-t border-zinc-100" />
        </div>
    );
}
